
import { UserSubscription } from "@/hooks/subscriptions/types";
import { formatDate } from "@/lib/formatters";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface UserSubscriptionDetailsProps {
  subscription: UserSubscription | null;
  onViewPlans: () => void;
}

export function UserSubscriptionDetails({ subscription, onViewPlans }: UserSubscriptionDetailsProps) {
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const { useCancelUserSubscriptionMutation } = useSubscriptions();
  const cancelSubscription = useCancelUserSubscriptionMutation();

  const formatPrice = (price: number | null) => {
    if (price === null) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(price / 100);
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    
    try {
      setIsCancelling(true);
      await cancelSubscription.mutateAsync(subscription.id);
      toast.success("Subscription cancelled successfully");
      setShowCancelDialog(false);
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      toast.error("Failed to cancel subscription. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  if (!subscription || !subscription.plan) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your Subscription</CardTitle>
          <CardDescription>You are currently on the Free plan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 text-muted-foreground mb-4">
            <AlertCircle className="h-5 w-5" />
            <span>You don't have an active subscription</span>
          </div>
          <p>Upgrade to access premium features and get more out of your contract management.</p>
        </CardContent>
        <CardFooter>
          <Button onClick={onViewPlans}>View Plans</Button>
        </CardFooter>
      </Card>
    );
  }

  const isActive = subscription.status === "active";
  const endDate = new Date(subscription.current_period_end);
  const today = new Date();
  const isExpiringSoon = isActive && endDate.getTime() - today.getTime() < 7 * 24 * 60 * 60 * 1000; // 7 days

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Subscription</CardTitle>
        <CardDescription>
          {isActive ? "Active subscription" : "Subscription information"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">{subscription.plan.name}</h3>
            <p className="text-sm text-muted-foreground">{subscription.plan.description}</p>
          </div>
          <Badge variant={isActive ? "default" : "outline"}>
            {subscription.status}
          </Badge>
        </div>

        <div className="border-t pt-4">
          <dl className="space-y-2">
            <div className="flex justify-between">
              <dt className="font-medium">Price</dt>
              <dd>{formatPrice(subscription.plan.monthly_price)}/month</dd>
            </div>
            <div className="flex justify-between">
              <dt className="font-medium">Billing Period</dt>
              <dd>{formatDate(new Date(subscription.current_period_start))} - {formatDate(endDate)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="font-medium">Status</dt>
              <dd className="flex items-center">
                {isActive ? (
                  <span className="flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="text-muted-foreground">{subscription.status}</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {isExpiringSoon && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-md text-sm flex items-center">
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
            Your subscription will expire soon on {formatDate(endDate)}.
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onViewPlans}>
          View Available Plans
        </Button>
        {isActive && (
          <Button 
            variant="ghost" 
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => setShowCancelDialog(true)}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              "Cancel Subscription"
            )}
          </Button>
        )}
      </CardFooter>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelSubscription} 
              className="bg-destructive text-destructive-foreground"
              disabled={isCancelling}
            >
              {isCancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Yes, Cancel"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
